// DeployExternalAssignTool.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * DeployExternalAssignTool
 *
 * Props:
 *  - backEndUrl (string): e.g., https://your-api.example.com
 *  - encodedState (string): the LTI deep-link state returned by Learn to your tool
 *  - defaultCount (number, optional): initial number of items to deploy (default 3)
 *  - defaultScore (number, optional): default scoreMaximum for created line items (default 10)
 *  - locale (string, optional): i18n locale key (default "en_US")
 *  - tenantId (string, optional): forwarded as "x-tenant-id" header to backend, if provided
 *
 * Behavior:
 *  - User chooses a count and provides a title for each item.
 *  - On deploy, builds IMS Deep Linking "content_items" (ltiResourceLink) payload,
 *    posts to `${backEndUrl}/lti/deep-link?state=${encodedState}`,
 *    then form-posts the returned JWT to the platform deepLinkReturnUrl.
 */

const MESSAGES = {
    en_US: {
        title: "Deploy External Assignments",
        howMany: "How many items?",
        rangeHelp: "Enter a number between 1 and 100.",
        table: { n: "#", itemTitle: "Title" },
        placeholders: (i) => `External Assignment ${i}`,
        buttons: {
            deploy: "Deploy to Blackboard",
            deploying: "Deploying…",
            retry: "Retry",
        },
        alerts: {
            missingConfig: "Missing required configuration (backEndUrl or encodedState).",
            enterOneTitle: "Please enter at least one title.",
            failed: "Deployment failed. Please try again.",
            invalidScore: "Score maximum must be a positive number.",
        },
        labels: {
            scoreMax: "Default points possible",
        },
    },
    en_AU: {},
};

const fallbackLocale = "en_US";
const getMessages = (loc) => ({ ...MESSAGES[fallbackLocale], ...(MESSAGES[loc] || {}) });

const normalizeBase = (u) => String(u || "").replace(/\/+$/, "");
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const DEFAULT_MAX_ITEMS = 100;

const DeployExternalAssignTool = ({
                                      backEndUrl,
                                      encodedState,
                                      defaultCount = 3,
                                      defaultScore = 10,
                                      locale = "en_US",
                                      tenantId,
                                  }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const [count, setCount] = useState(() => clamp(Number(defaultCount) || 3, 1, DEFAULT_MAX_ITEMS));
    const [scoreMax, setScoreMax] = useState(() => Math.max(0.01, Number(defaultScore) || 10));
    const [titles, setTitles] = useState(() =>
        Array.from({ length: clamp(Number(defaultCount) || 3, 1, DEFAULT_MAX_ITEMS) }, (_, i) =>
            M.placeholders ? M.placeholders(i + 1) : `External Assignment ${i + 1}`
        )
    );
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [nonce, setNonce] = useState(0); // for manual retries

    // keep titles array length in sync with count, seeding reasonable defaults for new rows
    useEffect(() => {
        setTitles((prev) => {
            const next = [...prev];
            if (count > prev.length) {
                for (let i = prev.length; i < count; i++) {
                    next[i] = M.placeholders ? M.placeholders(i + 1) : `External Assignment ${i + 1}`;
                }
            } else if (count < prev.length) {
                next.length = count;
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, nonce]);

    const canDeploy = useMemo(() => {
        const trimmed = titles.map((t) => (t || "").trim());
        const allFilled = trimmed.length > 0 && trimmed.every((t) => t.length > 0);
        return allFilled && Number.isFinite(scoreMax) && scoreMax > 0;
    }, [titles, scoreMax]);

    // Safer slug (URL path segment)
    const slugify = (s) =>
        (s || "")
            .toLowerCase()
            .trim()
            .replace(/[^\p{L}\p{N}\s._-]/gu, "") // keep letters, numbers, space, dot, underscore, hyphen
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            .replace(/^[-.]+|[-.]+$/g, "");

    const handleTitleChange = (idx, value) => {
        setTitles((prev) => {
            const next = [...prev];
            next[idx] = value;
            return next;
        });
    };

    const api = useMemo(() => {
        const base = normalizeBase(backEndUrl);
        return axios.create({
            baseURL: base || undefined,
            timeout: 20000,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(tenantId ? { "x-tenant-id": String(tenantId) } : {}),
            },
        });
    }, [backEndUrl, tenantId]);

    const buildItems = (trimmedTitles) => {
        return trimmedTitles.map((title) => {
            const slug = slugify(title) || "item";
            // ensure uniqueness in resourceId with a short hash-ish suffix
            const uniq = Math.random().toString(36).slice(2, 8);
            return {
                type: "ltiResourceLink",
                title,
                url: `${normalizeBase(backEndUrl)}/lti/launch/${slug}`,
                custom: {
                    assignmentName: title,
                    route: "ExternalAssignment",
                },
                lineItem: {
                    resourceId: `${slug}-${uniq}`,
                    label: `LTI Item: ${title}`,
                    scoreMaximum: Number(scoreMax),
                },
            };
        });
    };

    const formPostJwt = (deepLinkReturnUrl, jwt) => {
        // IMS Deep Linking expects "JWT" form field posted to the platform
        const form = document.createElement("form");
        form.method = "POST";
        form.action = deepLinkReturnUrl;
        form.enctype = "application/x-www-form-urlencoded";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "JWT";
        input.value = jwt;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
    };

    const handleDeploy = async () => {
        setMessage(null);

        if (!backEndUrl || !encodedState) {
            setMessage(M.alerts.missingConfig);
            return;
        }
        if (!Number.isFinite(scoreMax) || scoreMax <= 0) {
            setMessage(M.alerts.invalidScore);
            return;
        }

        const trimmed = titles.map((t) => (t || "").trim()).filter(Boolean);
        if (trimmed.length === 0) {
            setMessage(M.alerts.enterOneTitle);
            return;
        }

        const selectedItems = buildItems(trimmed);
        const payload = { selectedItems };

        try {
            setIsLoading(true);

            const res = await api.post(
                `/lti/deep-link?state=${encodeURIComponent(encodedState)}`,
                payload
            );

            const jwt = res?.data?.jwt;
            const deepLinkReturnUrl = res?.data?.deepLinkReturnUrl;

            if (!jwt || !deepLinkReturnUrl) {
                throw new Error("Backend response missing jwt or deepLinkReturnUrl.");
            }

            formPostJwt(deepLinkReturnUrl, jwt);
            // Note: platform will navigate away after form submit; no follow-up state necessary.
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Deployment failed:", err);
            setMessage(M.alerts.failed);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bb-container max-w-3xl mx-auto p-4">
            {message && (
                <div
                    className="bb-alert mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-800"
                    role="alert"
                    aria-live="polite"
                >
                    <div className="flex items-center justify-between gap-2">
                        <span>{message}</span>
                        <button
                            type="button"
                            className="text-sm underline"
                            onClick={() => setNonce((n) => n + 1)}
                        >
                            {M.buttons.retry}
                        </button>
                    </div>
                </div>
            )}

            <div className="bb-card rounded border p-4 mb-4">
                <label className="block font-medium mb-1" htmlFor="itemCount">
                    {M.howMany}
                </label>
                <input
                    id="itemCount"
                    type="number"
                    min={1}
                    max={DEFAULT_MAX_ITEMS}
                    value={count}
                    onChange={(e) => setCount(clamp(Number(e.target.value) || 1, 1, DEFAULT_MAX_ITEMS))}
                    className="bb-input w-32"
                    inputMode="numeric"
                    aria-describedby="countHelp"
                />
                <div id="countHelp" className="text-sm text-gray-600 mt-1">
                    {M.rangeHelp}
                </div>
            </div>

            <div className="bb-card rounded border p-4 mb-4">
                <label className="block font-medium mb-1" htmlFor="scoreMax">
                    {M.labels.scoreMax}
                </label>
                <input
                    id="scoreMax"
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={scoreMax}
                    onChange={(e) => setScoreMax(Math.max(0.01, Number(e.target.value) || 0))}
                    className="bb-input w-40"
                />
            </div>

            <div className="bb-card rounded border p-4 mb-4">
                <div className="bb-table w-full">
                    <div className="grid grid-cols-[60px_1fr] gap-3 font-medium border-b pb-2 mb-3">
                        <div>{M.table.n}</div>
                        <div>{M.table.itemTitle}</div>
                    </div>
                    {titles.map((t, idx) => (
                        <div key={idx} className="grid grid-cols-[60px_1fr] gap-3 items-center mb-2">
                            <div className="text-gray-700">{idx + 1}</div>
                            <input
                                type="text"
                                value={t}
                                onChange={(e) => handleTitleChange(idx, e.target.value)}
                                placeholder={M.placeholders ? M.placeholders(idx + 1) : `External Assignment ${idx + 1}`}
                                className="bb-input w-full"
                                aria-label={`${M.table.itemTitle} ${idx + 1}`}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <button
                onClick={handleDeploy}
                disabled={isLoading || !canDeploy}
                className={`bb-button px-4 py-2 rounded ${
                    isLoading || !canDeploy ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                } bg-blue-600 text-white`}
            >
                {isLoading ? M.buttons.deploying : M.buttons.deploy}
            </button>
        </div>
    );
};

export default DeployExternalAssignTool;
